// app/src/shared/plans-catalog.ts
export type TierSlug =
  | "free"
  | "hobbyist"
  | "starter"
  | "growth"
  | "pro"
  | "enterprise";

export interface PlanCatalogEntry {
  readonly slug: TierSlug;
  readonly name: string;
  // null = no displayed price (Enterprise = "Contact Sales"; Free renders as $0 since cents=0)
  readonly monthlyPriceCents: number | null;
  readonly annualPriceCents: number | null;
  readonly features: ReadonlyArray<string>;
  readonly rendersPerMonth: number;
  readonly maxConcurrent: number;
  readonly retentionMs: number;
  readonly isFree?: boolean;
  readonly isEnterprise?: boolean;
}

/**
 * Overage rate applied to every render past a plan's monthly quota.
 * Catalog-level constant (not per-tier today; if per-tier ever needed,
 * lift this onto `PlanCatalogEntry` later — YAGNI now).
 */
export const OVERAGE_RATE_CENTS_PER_RENDER = 5; // $0.05 / render

export const PLANS_CATALOG: ReadonlyArray<PlanCatalogEntry> = [
  {
    slug: "free",
    name: "Free",
    monthlyPriceCents: 0,
    annualPriceCents: null,
    features: [
      "100 renders/month",
      "2 concurrent renders",
      "24-hour retention",
    ],
    rendersPerMonth: 100,
    maxConcurrent: 2,
    retentionMs: 24 * 60 * 60 * 1000,
    isFree: true,
  },
  {
    slug: "hobbyist",
    name: "Hobbyist",
    monthlyPriceCents: 700,
    annualPriceCents: 7000,
    features: [
      "500 renders/month",
      "3 concurrent renders",
      "24-hour retention",
    ],
    rendersPerMonth: 500,
    maxConcurrent: 3,
    retentionMs: 24 * 60 * 60 * 1000,
  },
  {
    slug: "starter",
    name: "Starter",
    monthlyPriceCents: 1900,
    annualPriceCents: 19000,
    features: [
      "2,500 renders/month",
      "5 concurrent renders",
      "24-hour retention",
    ],
    rendersPerMonth: 2500,
    maxConcurrent: 5,
    retentionMs: 24 * 60 * 60 * 1000,
  },
  {
    slug: "growth",
    name: "Growth",
    monthlyPriceCents: 3900,
    annualPriceCents: 39000,
    features: [
      "8,000 renders/month",
      "10 concurrent renders",
      "48-hour retention",
    ],
    rendersPerMonth: 8000,
    maxConcurrent: 10,
    retentionMs: 48 * 60 * 60 * 1000,
  },
  {
    slug: "pro",
    name: "Pro",
    monthlyPriceCents: 7900,
    annualPriceCents: 79000,
    features: [
      "25,000 renders/month",
      "20 concurrent renders",
      "72-hour retention",
    ],
    rendersPerMonth: 25000,
    maxConcurrent: 20,
    retentionMs: 72 * 60 * 60 * 1000,
  },
  {
    slug: "enterprise",
    name: "Enterprise",
    // No displayed price — Enterprise is custom-quoted. Both nulls signal
    // "no price shown anywhere"; CTA falls through to Contact Sales.
    monthlyPriceCents: null,
    annualPriceCents: null,
    features: [
      "100,000+ renders/month",
      "Custom concurrency",
      "Extended retention",
    ],
    rendersPerMonth: 100000, // floor — actual quota override per-customer
    maxConcurrent: 50,
    retentionMs: 7 * 24 * 60 * 60 * 1000,
    isEnterprise: true,
  },
];

export function getCatalogEntry(slug: TierSlug): PlanCatalogEntry {
  const entry = PLANS_CATALOG.find((p) => p.slug === slug);
  if (!entry) throw new Error(`Unknown plan slug: ${slug}`);
  return entry;
}

// The paid subset is what gets sync'd into checkout's plans table.
export const PAID_PLAN_SLUGS: ReadonlyArray<TierSlug> = PLANS_CATALOG.filter(
  (p) => !p.isFree && !p.isEnterprise,
).map((p) => p.slug);
